import { CanvasAnnotation, CanvasRegion, ROI } from "types/imageAnnotations";
import { generateId } from "utils/generateId";
import { generateImageUrl } from "utils/generateImageUrl";

import { BOUNDING_BOXES_COLORS } from "./constants";
import { Dimensions } from "./hooks/useResize";
import { Point, Rectangle } from "./types";

/**
 * Calculates the size of a cross based on dimensions and zoom level.
 * Used in both downloadCanvasImage and MiddleCross component.
 *
 * @param {number} width - The width of the container
 * @param {number} height - The height of the container
 * @param {number} zoom - The zoom level (defaults to 1)
 * @returns {number} - The calculated cross size
 */
export function calculateCrossSize(width: number, height: number, zoom: number = 1): number {
  return Math.min(12 / zoom, Math.min(width, height) * 0.55);
}

export function calculateGeometry(prevPoint: Point, lastPoint: Point, canvasDimensions: Dimensions) {
  const imageWidth = canvasDimensions.width;
  const imageHeight = canvasDimensions.height;

  // Extracting the geometry using prevPoint and lastPoint and scaling it
  const left = prevPoint.x / imageWidth;
  const top = prevPoint.y / imageHeight;
  const right = lastPoint.x / imageWidth;
  const bottom = lastPoint.y / imageHeight;

  return {
    version: "1",
    left,
    top,
    right,
    bottom,
  };
}

/**
 * Clamps a point to the boundaries of a canvas.
 *
 * @param {Point} point - The point to clamp.
 * @param {HTMLCanvasElement} canvas - The canvas to which to clamp the point.
 * @returns {Point} - The clamped point.
 */
export function clampPointToCanvas(point: Point, canvas: Dimensions): Point {
  return {
    x: Math.min(Math.max(0, point.x), canvas.width),
    y: Math.min(Math.max(0, point.y), canvas.height),
  };
}

export function parseGeometryToPoints(
  region: CanvasRegion,
  canvasDimensions: Dimensions
): { start: Point; end: Point } {
  const imageWidth = canvasDimensions.width;
  const imageHeight = canvasDimensions.height;

  const start: Point = {
    x: region.start.x * imageWidth,
    y: region.start.y * imageHeight,
  };

  const end: Point = {
    x: region.end.x * imageWidth,
    y: region.end.y * imageHeight,
  };

  return {
    start,
    end,
  };
}

export function rotateDimensions(dimensions: Dimensions, rotateDimensions: boolean): Dimensions {
  if (rotateDimensions) {
    return {
      width: dimensions.height,
      height: dimensions.width,
    };
  }
  return dimensions;
}

export const rotatedDimension = (
  dimensions: Dimensions,
  {
    property,
    rotateDimensions,
  }: {
    property: keyof Dimensions;
    rotateDimensions: boolean;
  }
) => {
  const otherDimensionsKey: Record<keyof Dimensions, keyof Dimensions> = {
    width: "height",
    height: "width",
  };
  if (rotateDimensions) return dimensions[otherDimensionsKey[property]];
  return dimensions[property];
};

export const getIQImage = (iqId: string) => {
  const img = new Image();
  img.src = generateImageUrl(iqId);
  return img;
};

export const getImageSizeToContainImage = (img: HTMLImageElement, maxHeightPercentage: number = 0.17) => {
  // This function calculates the size of the image to fit within the screen while maintaining its aspect ratio.
  // It's similar to do this:
  // <img style={{objectFit: "contain",height: "17vh", width: "100%",}}

  // Getting the screen size
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  // restricting the image size to 17vh of height
  const maxHeight = Math.floor(screenHeight * maxHeightPercentage);

  // Calculate the aspect ratio of the original image
  const aspectRatio = img.width / img.height;

  // Calculate dimensions that maintain aspect ratio within constraints (this is the "contain" behavior)
  let newWidth;
  let newHeight;

  // If the image is wider than it is tall
  if (aspectRatio > 1) {
    // Width is the limiting factor
    newWidth = Math.min(screenWidth, maxHeight * aspectRatio);
    newHeight = newWidth / aspectRatio;
  } else {
    // Height is the limiting factor
    newHeight = maxHeight;
    newWidth = newHeight * aspectRatio;
  }

  // Ensure height doesn't exceed maxHeight
  if (newHeight > maxHeight) {
    newHeight = maxHeight;
    newWidth = newHeight * aspectRatio;
  }
  return { width: newWidth, height: newHeight };
};

export function getDistance(point1: Point, point2: Point) {
  return Math.sqrt((point2.x - point1.x) ** 2 + (point2.y - point1.y) ** 2);
}

export function getCenter(point1: Point, point2: Point) {
  return {
    x: (point1.x + point2.x) / 2,
    y: (point1.y + point2.y) / 2,
  };
}

export function clampInnerRectangle(outer: Rectangle, inner: Rectangle): Rectangle {
  // Ensure minimum scale to cover the outer rectangle
  const minScaleX = outer.width / inner.width;
  const minScaleY = outer.height / inner.height;
  const newScale = Math.max(inner.scale, Math.max(minScaleX, minScaleY));

  // Calculate scaled size of the inner rectangle
  const scaledWidth = inner.width * newScale;
  const scaledHeight = inner.height * newScale;

  const clampedX = Math.min(outer.x, Math.max(outer.x + outer.width - scaledWidth, inner.x));
  const clampedY = Math.min(outer.y, Math.max(outer.y + outer.height - scaledHeight, inner.y));

  return {
    ...inner,
    x: clampedX,
    y: clampedY,
    scale: newScale,
  };
}

export function isInnerRectangleContained(outer: Rectangle, inner: Rectangle) {
  const isContainedInX = outer.width >= inner.width * inner.scale;
  const isContainedInY = outer.height >= inner.height * inner.scale;

  return {
    isContainedInX,
    isContainedInY,
  };
}

// Function to normalize rotation based on flipX and flipY and the current rotation
// The idea is to use this function to get the correct rotation value for a canvas element
// this will maintain the object straight when flipping or rotating
// Currently used by the delete icon and the label of the bounding box, because these objects
// must be straight when flipping or rotating to improve the user experience and readability
export const normalizedRotation = (rotation: number, flipX: boolean, flipY: boolean) => {
  if (!flipX && !flipY) {
    return 360 - rotation;
  }
  if (!flipX && flipY) {
    return rotation;
  }
  if (flipX && !flipY) {
    return 360 + rotation;
  }
  if (flipX && flipY) {
    if (rotation === 0 || rotation === 180) {
      return rotation;
    }
    if (rotation === 90) {
      return rotation + 180;
    }
    if (rotation === 270) {
      return rotation + 180;
    }
  }
  return rotation;
};

// Helper function to get the transformed coordinates
export const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(v, max));

function getCanvasAnnotationColorForIndex(index: number): string {
  return BOUNDING_BOXES_COLORS[index % BOUNDING_BOXES_COLORS.length]
}

export function getCanvasAnnotationsFromROI(roiAnnotation: ROI, index: number): CanvasAnnotation {
  return {
    start: {
      x: roiAnnotation.init_args.geometry.init_args.left,
      y: roiAnnotation.init_args.geometry.init_args.top,
    },
    end: {
      x: roiAnnotation.init_args.geometry.init_args.right,
      y: roiAnnotation.init_args.geometry.init_args.bottom,
    },
    label: roiAnnotation.init_args.label,
    id: generateId(),
    shape: "rectangle",
    strokeColor: getCanvasAnnotationColorForIndex(index)
  }
}

export function getCanvasAnnotationsFromROIs(roiAnnotations: ROI[]): CanvasAnnotation[] {
	return roiAnnotations.map((roiAnnotation, index) => getCanvasAnnotationsFromROI(roiAnnotation, index))
}

/**
 * Resizes a canvas to ensure its height is at least `minHeight`, preserving
 * aspect ratio.
 *
 * @param {HTMLCanvasElement} canvas - The source canvas to resize.
 * @param {number} minHeight - The minimum height to resize to.
 * @returns {HTMLCanvasElement} A new canvas element with the resized dimensions.
 */
export function resizeCanvasToMinHeight(canvas: HTMLCanvasElement, minHeight: number): HTMLCanvasElement {
  const scale = canvas.height < minHeight ? minHeight / canvas.height : 1;

  const resizedCanvas = document.createElement("canvas");
  resizedCanvas.width = canvas.width * scale;
  resizedCanvas.height = canvas.height * scale;

  const ctx = resizedCanvas.getContext("2d");

  if (ctx) {
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(canvas, 0, 0, resizedCanvas.width, resizedCanvas.height);
  }

  return resizedCanvas;
}

/**
 * Converts an HTMLImageElement to a canvas with the same dimensions and content.
 *
 * @param {HTMLImageElement} image - The image to convert.
 * @returns {HTMLCanvasElement} A canvas containing the image's content.
 */
export function imageToCanvas(image: HTMLImageElement): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const ctx = canvas.getContext("2d");

  if (ctx) {
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(image, 0, 0);
  }

  return canvas;
}
